// Fork of controls/CollabControlPage.tsx for the work-step collab wizard.
//
// Context documents are sourced the same way as on the controls page: from the
// generation session during initial generation, and from the work step's own
// document set (useWorkStepDocuments) once a work step exists (refine mode).
// No PipelineScope exists for a generation session or a work step, so this page
// mounts no processing toast; every attached document chips, in-flight ones
// included.
import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Sparkles, AlertCircle, Undo2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "../../../components/ui/resizable";
import {
  useCollabWorkStepSessionDetail,
  useCollabWorkStepSSE,
  useCollabWorkStepSessionDocuments,
  useCreateCollabSessionFromWorkStep,
  useFinalizeReviseWorkStepSession,
} from "../../../hooks/useCollabWorkStep";
import { useDraftPendingColumnsOnOpen } from "../../../hooks/useDraftPendingColumnsOnOpen";
import { useWorkStepDocuments } from "../../../hooks/useWorkStepDocuments";
import {
  CollabMessage,
  CollabPhase,
  WorkStepDraftPlan,
  WorkStepPlanPatch,
  PHASE_DISPLAY,
} from "../../../models/audit/collabWorkStepSession";
import { CollabPhaseIndicator } from "../../../components/audit/collab/CollabPhaseIndicator";
import { CollabContextDocument } from "../../../models/audit/collabContextDocument";
import { CollabChatPanel } from "../../../components/audit/collab/CollabChatPanel";
import { RefineOverwriteConfirmDialog } from "../../../components/audit/collab/RefineOverwriteConfirmDialog";
import { WorkStepEditedSinceSessionStart } from "../../../models/audit/collabWorkStepSession";
import {
  WorkStepPlanDisplay,
  PlanRevealTarget,
} from "./components/collab/WorkStepPlanDisplay";
import { LoadingSpinner } from "../../../components/ui/loadingSpinner/loadingSpinner";
import {
  diffPlanFreeText,
  FieldDiff,
} from "./components/collab/planFreeTextDiff";
import { toast } from "sonner";
import { toastPendingTestSkipped } from "./pendingTestSkippedCopy";
import { toastDroppedAttributeLinks } from "./droppedAttributeLinksCopy";
import { toastOptroFieldsNotCarried } from "../../../lib/optroFieldsNotCarried";
import { isRefusedRequest } from "../../../api/sseClient";

export const CollabWorkStepPage = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  // Revise with AI mode is driven by query params (?mode=refine&id=<workStepId>).
  // On entry we open a real backend session seeded from that work step (already
  // in the iterating phase), so the chat, plan panel, and agent loop all use
  // the shared collab machinery. `refineSessionId` holds the id once created.
  const [searchParams] = useSearchParams();
  // The URL flag drives session CREATION only (this page seeds a revise session
  // when entered via ?mode=refine). Classification of an already-loaded session
  // is derived below from the backend's sourceWorkStepId, so a revise session
  // reached by a plain /collab/:sessionId link still renders as refine.
  const urlRefineMode = searchParams.get("mode") === "refine";
  const urlRefineWorkStepId = searchParams.get("id");

  const { createFromWorkStep } = useCreateCollabSessionFromWorkStep();
  const { finalizeRevise } = useFinalizeReviseWorkStepSession();
  const [refineSessionId, setRefineSessionId] = useState<string | null>(null);
  const [refineCreateFailed, setRefineCreateFailed] = useState(false);
  const refineSessionRequested = useRef(false);

  // The session the collab hooks actually drive: the URL param for a normal
  // session, or the seeded revise session in refine mode.
  const effectiveSessionId = urlRefineMode
    ? refineSessionId
    : (sessionId ?? null);

  // Session state
  const {
    session,
    loading: sessionLoading,
    error: sessionError,
    refetch: refetchSession,
  } = useCollabWorkStepSessionDetail(effectiveSessionId);

  // Refine-ness for everything except session creation: the URL says so, or the
  // loaded session is bound to a source work step (the backend's authoritative
  // revise-vs-generation discriminator).
  const isRefineMode = urlRefineMode || session?.sourceWorkStepId != null;
  const refineWorkStepId =
    urlRefineWorkStepId ?? session?.sourceWorkStepId ?? null;

  // Context documents follow the doc's lifecycle: they start on the generation
  // session (initial gen) and are reassigned onto the work step at generation, so
  // a doc lives in exactly one place. Show — and add/remove against — the work
  // step's own set once a work step exists (refine mode), otherwise the session's
  // set. The panel is identical in both stages; only the data source differs.
  const relevantWorkStepId = isRefineMode ? refineWorkStepId : null;
  const workStepDocs = useWorkStepDocuments(relevantWorkStepId);
  // Only one source is live at a time; null disables the inactive hook's fetch.
  const sessionDocs = useCollabWorkStepSessionDocuments(
    relevantWorkStepId ? null : effectiveSessionId,
  );
  const {
    documents: activeDocuments,
    mutating: contextDocumentsUploading,
    attachDocuments: attachActiveDocuments,
    removeDocument: removeActiveDocument,
  } = relevantWorkStepId ? workStepDocs : sessionDocs;

  // The chat panel's context-document chips take the collab upload shape. The
  // junction id rides in as `id`, so removal calls DELETE with the
  // work-step-document id. `state` is narrowed to the chip's four states; a
  // failed parse must read as failed, not as usable context.
  // Every attached document chips, in-flight ones included: this page mounts
  // no processing toast (no PipelineScope covers a generation session or a
  // work step), so a hidden in-flight chip would have nowhere to surface
  // until a manual reload.
  const contextDocuments: CollabContextDocument[] = activeDocuments.map(
    (doc) => ({
      id: doc.workStepDocumentId,
      fileName: doc.fileName,
      fileType: doc.fileType,
      // The endpoint sends the document's raw parse state. A still-parsing video
      // (video_pending_parse) is in-flight, not done; anything genuinely unknown
      // fails visibly rather than masquerading as ready.
      state:
        doc.state === "pending" ||
        doc.state === "processing" ||
        doc.state === "failed" ||
        doc.state === "ready"
          ? doc.state
          : doc.state === "video_pending_parse"
            ? "processing"
            : "failed",
    }),
  );

  const handleRemoveContextDocument = useCallback(
    (workStepDocumentId: string) => removeActiveDocument(workStepDocumentId),
    [removeActiveDocument],
  );

  // SSE hooks
  const {
    isStreaming,
    streamingSilent,
    streamingContent,
    agentEvents,
    heartbeatCounter,
    isStalled,
    isReconnecting,
    error: streamError,
    startPlanning,
    sendMessage,
    generateWorkStep,
    updatePlan,
    draftPendingColumns,
    undo,
    cancel,
  } = useCollabWorkStepSSE(effectiveSessionId);

  // Local state
  const [messages, setMessages] = useState<CollabMessage[]>([]);
  const [currentPlan, setCurrentPlan] = useState<WorkStepDraftPlan | null>(
    null,
  );
  const [inputValue, setInputValue] = useState("");
  const [phase, setPhase] = useState<CollabPhase>("understanding");
  // The context-documents section only makes sense once the plan is on screen
  // and editable (ITERATING): during understanding/planning the agent is already
  // mid-analysis, so adding or removing grounding docs there is a lie — the turn
  // in flight won't see the change.
  const showContextDocumentsSection = phase === "iterating";
  const [isGenerating, setIsGenerating] = useState(false);
  // Set when finalize found edits made to the work step since this refine
  // session began; the user confirms the overwrite or keeps refining.
  const [overwriteConflict, setOverwriteConflict] =
    useState<WorkStepEditedSinceSessionStart | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [undoCount, setUndoCount] = useState(0);

  // Free-text before/after diffs for each revision, keyed by the assistant
  // message that reported the change. Rendered inline in the conversation.
  const [revisionDiffs, setRevisionDiffs] = useState<
    Record<string, FieldDiff[]>
  >({});
  // Diffs from the most recent revision turn only, shown inline on the changed
  // fields in the plan panel. Cleared when a new turn starts or on undo.
  const [latestTurnDiffs, setLatestTurnDiffs] = useState<FieldDiff[]>([]);
  // The plan as it stood before that turn, so the panel can render removed
  // items as in-place ghosts. Lives and dies with latestTurnDiffs.
  const [latestTurnBasePlan, setLatestTurnBasePlan] =
    useState<WorkStepDraftPlan | null>(null);
  // Field the user asked the plan panel to reveal (chat chip click): drives the
  // one-shot scroll only.
  const [revealTarget, setRevealTarget] = useState<PlanRevealTarget | null>(
    null,
  );
  // Field kept highlighted (amber ring) after a chip click. Unlike revealTarget this
  // persists — until another field is revealed or a new edit/turn clears it.
  const [highlightedField, setHighlightedField] = useState<string | null>(null);
  const handleNavigateToField = useCallback((fieldKey: string) => {
    // Bump the nonce so re-clicking the same chip re-scrolls (a one-shot).
    setRevealTarget((prev) => ({
      key: fieldKey,
      nonce: (prev?.nonce ?? 0) + 1,
    }));
    // The highlight moves to this field and stays until the next reveal or edit.
    setHighlightedField(fieldKey);
  }, []);

  // The reveal SCROLL is a one-shot: clear revealTarget once it has run, so later
  // remounts (agent turns, undo, tab switches, the fold-out remount) don't re-scroll
  // to the field. The amber highlight is separate (highlightedField) and persists.
  useEffect(() => {
    if (!revealTarget) return;
    const timer = setTimeout(() => setRevealTarget(null), 2000);
    return () => clearTimeout(timer);
  }, [revealTarget]);
  const planBeforeTurnRef = useRef<WorkStepDraftPlan | null>(null);
  const planAfterTurnRef = useRef<WorkStepDraftPlan | null>(null);
  // Diff awaiting re-keying: after the post-turn refetch replaces messages with
  // the backend copy (new ids), we re-attach the diff to that assistant message.
  const pendingRevisionDiffRef = useRef<FieldDiff[] | null>(null);
  // Mirror of isStreaming for effects that must READ it without re-running on it.
  const isStreamingRef = useRef(isStreaming);
  isStreamingRef.current = isStreaming;

  // Entered via a refine URL: open the seeded revise session exactly once. Keyed
  // on the URL flag, not derived refine-ness — a directly-linked revise session
  // is already seeded and must not spawn a second one.
  useEffect(() => {
    if (!urlRefineMode || refineSessionRequested.current) return;
    // A refine URL with no work-step id (hand-edited) can never seed a session;
    // fail fast to the error card instead of spinning on the loader forever.
    if (!urlRefineWorkStepId) {
      setRefineCreateFailed(true);
      return;
    }
    refineSessionRequested.current = true;
    void createFromWorkStep(urlRefineWorkStepId).then((newSessionId) => {
      if (newSessionId) {
        setRefineSessionId(newSessionId);
      } else {
        setRefineCreateFailed(true);
      }
    });
  }, [urlRefineMode, urlRefineWorkStepId, createFromWorkStep]);

  // Sync session data to local state when loaded (both create and refine).
  //
  // Never while a turn is streaming: a refetch landing mid-turn (or the effect
  // firing on the streaming flip itself) would clobber the optimistic transcript —
  // the just-sent user message — with the pre-turn server copy, and consume the
  // pending diff against a message list whose last assistant message is the
  // PREVIOUS turn's. The post-turn refetch always changes `session`, so this
  // re-runs once everything has settled. Read through a ref so the streaming flip
  // doesn't itself re-run the effect against a stale session.
  useEffect(() => {
    if (!session || isStreamingRef.current) return;

    setMessages(session.messages);
    setCurrentPlan(session.currentPlan);
    // A stream can fail locally before the server persists any failure (an
    // in-stream error on a cleanly closed stream still fires onComplete, whose
    // refetch returns a session that isn't failed). Adopting that non-terminal
    // server phase would erase the failed card one round-trip after it renders,
    // so keep the local failure until the server reports a terminal phase.
    // Retry releases the guard itself: handleStartPlanning/handleGenerateWorkStep
    // set the phase synchronously before streaming.
    setPhase((prev) =>
      prev === "failed" &&
      session.phase !== "failed" &&
      session.phase !== "complete"
        ? prev
        : session.phase,
    );
    setCanUndo(session.canUndo ?? false);
    setUndoCount(session.undoCount ?? 0);

    // Re-key a just-computed revision diff onto the backend assistant message
    // (the refetch above replaced the client-added message and its id).
    if (pendingRevisionDiffRef.current) {
      const diffs = pendingRevisionDiffRef.current;
      pendingRevisionDiffRef.current = null;
      const lastAssistant = [...session.messages]
        .reverse()
        .find((message) => message.role === "assistant");
      if (lastAssistant?.kind === "clarification") {
        // Asking is terminal: the backend discarded every plan change from the
        // turn, so diffs computed from the streamed (pre-discard) plan would
        // show ghosts and strikethroughs for changes that no longer exist.
        setLatestTurnDiffs([]);
        setLatestTurnBasePlan(null);
      } else if (lastAssistant) {
        setRevisionDiffs((prev) => ({ ...prev, [lastAssistant.id]: diffs }));
      }
    }

    // A create session that completed navigates to the finished work step. Refine
    // finalize (writing back to the source work step) is a separate later step.
    if (
      !isRefineMode &&
      session.phase === "complete" &&
      session.finalWorkStepId
    ) {
      toast.success("Work step created successfully!");
      navigate(`/audit/work-steps/${session.finalWorkStepId}`);
    }
  }, [session, navigate, isRefineMode]);

  // Auto-start planning if in understanding phase and no plan exists
  useEffect(() => {
    if (
      !isRefineMode &&
      session &&
      (session.phase === "understanding" || session.phase === "planning") &&
      !session.currentPlan &&
      !isStreaming
    ) {
      handleStartPlanning();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  const handleStartPlanning = useCallback(async () => {
    setPhase("planning");

    await startPlanning({
      onChunk: () => {
        // Content is handled by the streaming state
      },
      onPlanReady: (plan) => {
        setCurrentPlan(plan);
        setPhase("iterating");
        // Add a system message
        const assistantMessage: CollabMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content:
            "I've analyzed your documents and created a draft plan. Review the plan on the right and let me know if you'd like any changes.",
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, assistantMessage]);
      },
      onError: (error) => {
        setPhase("failed");
        console.error("Planning error:", error);
      },
      onComplete: () => {
        // Refetch to get the latest session state
        refetchSession({ backgroundFetch: true });
      },
    });
  }, [startPlanning, refetchSession]);

  const sendText = useCallback(
    async (text: string) => {
      if (!text.trim() || isStreaming) return;

      const userMessage: CollabMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: text.trim(),
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setInputValue("");

      // Snapshot the plan before this turn so we can diff it against the result.
      planBeforeTurnRef.current = currentPlan;
      planAfterTurnRef.current = null;
      // The panel's inline diffs describe the previous turn; drop them while the
      // new revision streams so they can't disagree with the incoming plan.
      setLatestTurnDiffs([]);
      setLatestTurnBasePlan(null);
      // A new turn is a "new chat" — drop any lingering field highlight.
      setHighlightedField(null);

      await sendMessage(text.trim(), {
        onChunk: () => {
          // Content is handled by the streaming state
        },
        onPlanUpdated: (plan) => {
          setCurrentPlan(plan);
          planAfterTurnRef.current = plan;
        },
        onComplete: (assistantContent) => {
          const before = planBeforeTurnRef.current;
          const after = planAfterTurnRef.current;
          const diffs = before && after ? diffPlanFreeText(before, after) : [];
          planAfterTurnRef.current = null;
          // Survives the refetch below, which re-keys messages to backend ids.
          pendingRevisionDiffRef.current = diffs.length > 0 ? diffs : null;
          setLatestTurnDiffs(diffs);
          setLatestTurnBasePlan(diffs.length > 0 ? before : null);

          if (assistantContent || diffs.length > 0) {
            const assistantMessage: CollabMessage = {
              id: crypto.randomUUID(),
              role: "assistant",
              content: assistantContent || "Updated the plan.",
              timestamp: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, assistantMessage]);
            if (diffs.length > 0) {
              setRevisionDiffs((prev) => ({
                ...prev,
                [assistantMessage.id]: diffs,
              }));
            }
          }
          // Refetch to sync with backend
          refetchSession({ backgroundFetch: true });
        },
        onError: (error) => {
          console.error("Message error:", error);
        },
      });
    },
    [isStreaming, sendMessage, refetchSession, currentPlan],
  );

  const handleSendMessage = useCallback(async () => {
    await sendText(inputValue);
  }, [sendText, inputValue]);

  // Answering a clarification is just the next user message; the buttons only
  // save typing the chosen option out.
  const handleAnswerClarification = useCallback(
    async (option: string) => {
      await sendText(option);
    },
    [sendText],
  );

  // Refine finalize: persist the revised plan back onto the source work step
  // (ID-stable, in place). On failure, keep the pane open so edits aren't lost;
  // if the work step changed since the session began, ask before overwriting.
  const finalizeRefine = useCallback(
    async (confirmOverwrite: boolean) => {
      if (!effectiveSessionId || !refineWorkStepId) {
        navigate(
          refineWorkStepId
            ? `/audit/work-steps/${refineWorkStepId}`
            : "/audit/work-steps",
        );
        return;
      }
      setIsGenerating(true);
      const outcome = await finalizeRevise(
        effectiveSessionId,
        refineWorkStepId,
        confirmOverwrite,
      );
      switch (outcome?.kind) {
        case "finalized":
          setOverwriteConflict(null);
          toast.success("Work step updated successfully!");
          if (outcome.pendingTestSkipped) {
            toastPendingTestSkipped(outcome.pendingTestSkipped);
          }
          toastDroppedAttributeLinks(outcome.droppedAttributeLinks);
          navigate(`/audit/work-steps/${outcome.workStepId}`);
          break;
        case "editedSinceStart":
          setOverwriteConflict(outcome.conflict);
          setIsGenerating(false);
          break;
        default:
          setIsGenerating(false);
      }
    },
    [effectiveSessionId, refineWorkStepId, finalizeRevise, navigate],
  );

  const handleGenerateWorkStep = useCallback(async () => {
    if (isRefineMode) {
      await finalizeRefine(false);
      return;
    }

    setIsGenerating(true);
    setPhase("generating");

    await generateWorkStep({
      onChunk: () => {
        // Content is handled by the streaming state
      },
      onWorkStepCreated: (workStepId) => {
        toast.success("Work step created successfully!");
        navigate(`/audit/work-steps/${workStepId}`);
      },
      onPendingTestSkipped: (reason) => {
        toastPendingTestSkipped(reason);
      },
      onOptroFieldsNotCarried: () => toastOptroFieldsNotCarried("workStep"),
      onError: (error, status) => {
        setIsGenerating(false);
        // The server answers 400, before anything runs, for any plan or session
        // it won't generate, so the session is unchanged. Only the refetch says
        // which case this is: an iterating session keeps its editable plan, and
        // a failed or complete one shows as such.
        if (isRefusedRequest(status)) {
          setPhase("iterating");
          refetchSession({ backgroundFetch: true });
          return;
        }
        setPhase("failed");
        console.error("Generation error:", error);
      },
      onComplete: () => {
        setIsGenerating(false);
        // Refetch to get final state
        refetchSession({ backgroundFetch: true });
      },
    });
  }, [
    generateWorkStep,
    navigate,
    refetchSession,
    isRefineMode,
    finalizeRefine,
  ]);

  const handleUndo = useCallback(async () => {
    const result = await undo();
    if (result) {
      setCurrentPlan(result.plan);
      setCanUndo(result.canUndo);
      setUndoCount(result.undoCount);
      // The undone revision's "new" text no longer exists, so its inline
      // strikethroughs would misdescribe the restored plan.
      setLatestTurnDiffs([]);
      setLatestTurnBasePlan(null);
    }
  }, [undo]);

  const handlePlanChange = useCallback(
    async (patch: WorkStepPlanPatch) => {
      // A direct edit is a "new edit" — drop any lingering field highlight.
      setHighlightedField(null);
      await updatePlan(patch, null, false, {
        onPlanUpdated: (plan) => {
          setCurrentPlan(plan);
        },
        onComplete: () => {
          refetchSession({ backgroundFetch: true });
        },
        onError: (error) => {
          console.error("Plan update error:", error);
        },
      });
    },
    [updatePlan, refetchSession],
  );

  const { noticeIfDraftingStopped } = useDraftPendingColumnsOnOpen({
    sessionId: session?.id,
    seededColumnsToDraft: session?.seededColumnsToDraft ?? false,
    draftPendingColumns,
    onPlanUpdated: setCurrentPlan,
    refetchSession,
  });

  const handleCancel = useCallback(() => {
    noticeIfDraftingStopped();
    cancel();
  }, [noticeIfDraftingStopped, cancel]);

  // Loading state — includes opening the seeded revise session in refine mode.
  // `!session && !sessionError` (rather than `sessionLoading && !session`) keeps us
  // on the loader across the gap between createFromWorkStep resolving (refineSessionId
  // set) and useCollabWorkStepSessionDetail's fetch effect flipping loading back on — that
  // gap is a committed render where loading is briefly false and would otherwise
  // flash the error card. We leave preparing only once the session lands or errors.
  const isPreparingSession = isRefineMode
    ? !refineCreateFailed &&
      (!effectiveSessionId || (!session && !sessionError))
    : sessionLoading && !session;
  if (isPreparingSession) {
    return (
      <div className="h-full flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // Error state. In refine mode the seeded session can fail at two points: the
  // create call itself (refineCreateFailed), or the detail fetch afterwards
  // (sessionError). A merely-absent session is still preparing (handled above), so
  // the error branch keys on sessionError — not `!session` — to avoid the flash;
  // both failure points must land here, not fall through to an empty main UI.
  if (
    (isRefineMode && (refineCreateFailed || !!sessionError)) ||
    (!isRefineMode && (sessionError || !session))
  ) {
    return (
      <div className="h-full flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Error Loading Session
            </CardTitle>
            <CardDescription>
              {sessionError || "Session not found"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate("/audit/work-steps")}
              variant="outline"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Work Steps
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Failed state
  if (phase === "failed") {
    return (
      <div className="h-full flex flex-col">
        <div className="p-4 border-b bg-white">
          <Button
            onClick={() => navigate("/audit/work-steps")}
            variant="ghost"
            size="sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                Session Failed
              </CardTitle>
              <CardDescription>
                {streamError ||
                  session?.errorMessage ||
                  "An error occurred during the session"}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex gap-2">
              <Button
                onClick={() => navigate("/audit/work-steps/create/manual")}
                variant="outline"
              >
                Start Over
              </Button>
              {/* Retry re-runs the stage that failed: no plan yet means planning
                  died, otherwise generation did. Refetching the session alone
                  never restarts anything — the auto-start effect is keyed on
                  session.id, which a retry doesn't change. */}
              <Button
                onClick={() =>
                  currentPlan ? handleGenerateWorkStep() : handleStartPlanning()
                }
              >
                Retry
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const canIterate = phase === "iterating" && !isStreaming && !isGenerating;
  const canGenerate =
    phase === "iterating" && currentPlan && !isStreaming && !isGenerating;

  return (
    <div className="absolute inset-0 flex flex-col bg-stone-50 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-5 flex-shrink-0">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            {isRefineMode ? (
              <Button
                onClick={() =>
                  navigate(
                    refineWorkStepId
                      ? `/audit/work-steps/${refineWorkStepId}`
                      : "/audit/work-steps",
                  )
                }
                variant="outline"
                size="sm"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Abandon Changes
              </Button>
            ) : (
              <Button
                onClick={() => navigate("/audit/work-steps")}
                variant="ghost"
                size="sm"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
            <div className="flex items-center gap-2">
              {canUndo && canIterate && (
                <Button
                  onClick={handleUndo}
                  variant="outline"
                  className="gap-2"
                  disabled={isStreaming}
                >
                  <Undo2 className="h-4 w-4" />
                  Undo{undoCount > 0 && ` (${undoCount})`}
                </Button>
              )}
              {canGenerate && (
                <Button onClick={handleGenerateWorkStep} className="gap-2">
                  <Sparkles className="h-4 w-4" />
                  {isRefineMode ? "Save Changes" : "Generate Work Step"}
                </Button>
              )}
            </div>
          </div>

          {/* Phase Indicator */}
          {!isRefineMode && (
            <div className="mt-4">
              <CollabPhaseIndicator
                currentPhase={phase}
                isStreaming={isStreaming}
              />
            </div>
          )}
        </div>
      </div>

      {/* Main Content - Split Layout */}
      <div className="flex-1 min-h-0 overflow-hidden px-6 pb-12 pt-0">
        <ResizablePanelGroup
          autoSaveId="collab-work-step-split"
          direction="horizontal"
          className="h-full max-w-7xl mx-auto"
        >
          {/* Left Panel - Chat */}
          <ResizablePanel
            id="chat-panel"
            order={1}
            defaultSize={50}
            minSize={30}
          >
            <Card className="flex h-full flex-col min-h-0 overflow-hidden">
              <CardHeader className="px-6 py-4 space-y-0.5 flex-shrink-0 border-b border-stone-200">
                <div className="text-xs font-medium text-stone-500">
                  Conversation
                </div>
                <CardTitle className="text-base font-semibold">
                  {PHASE_DISPLAY[phase].description}
                </CardTitle>
              </CardHeader>
              <div className="flex-1 min-h-0 overflow-hidden">
                <CollabChatPanel
                  messages={messages}
                  streamingContent={isGenerating ? "" : streamingContent}
                  // During generation the full-screen overlay is the progress
                  // surface; a second typing bubble in the transcript is noise.
                  // Silent plan patches (direct edits and restores, no
                  // feedback) apply without an agent turn — no bubble either;
                  // the input still locks via `disabled` during the ack.
                  isStreaming={isStreaming && !isGenerating && !streamingSilent}
                  agentEvents={agentEvents}
                  heartbeatCounter={heartbeatCounter}
                  isStalled={isStalled}
                  inputValue={inputValue}
                  onInputChange={setInputValue}
                  onSend={handleSendMessage}
                  onCancel={handleCancel}
                  revisionDiffs={revisionDiffs}
                  onNavigateToField={handleNavigateToField}
                  onAnswerClarification={handleAnswerClarification}
                  disabled={!canIterate}
                  placeholder={
                    phase === "iterating"
                      ? "Suggest changes to the plan..."
                      : "Waiting for plan to be ready..."
                  }
                  contextDocuments={
                    showContextDocumentsSection ? contextDocuments : undefined
                  }
                  onAddContextDocuments={
                    showContextDocumentsSection
                      ? attachActiveDocuments
                      : undefined
                  }
                  onRemoveContextDocument={
                    showContextDocumentsSection
                      ? handleRemoveContextDocument
                      : undefined
                  }
                  contextDocumentsUploading={contextDocumentsUploading}
                />
              </div>
            </Card>
          </ResizablePanel>

          <ResizableHandle withHandle className="mx-3 bg-transparent" />

          {/* Right Panel - Plan Display */}
          <ResizablePanel
            id="plan-panel"
            order={2}
            defaultSize={50}
            minSize={30}
          >
            <div className="flex h-full flex-col min-h-0 overflow-hidden">
              {currentPlan ? (
                <WorkStepPlanDisplay
                  plan={currentPlan}
                  editable={canIterate}
                  onPlanChange={handlePlanChange}
                  disabled={isStreaming}
                  headerLabel={isRefineMode ? "Refine Work Step" : "Draft Plan"}
                  saving={isStreaming && streamingSilent}
                  fieldDiffs={latestTurnDiffs}
                  previousPlan={latestTurnBasePlan}
                  revealTarget={revealTarget}
                  highlightedFieldKey={highlightedField}
                  optroLockedFields={session?.optroLockedFields}
                />
              ) : (
                <Card className="flex-1 flex items-center justify-center">
                  <div className="text-center p-8">
                    {isStreaming || phase === "planning" ? (
                      <>
                        <Loader2 className="h-8 w-8 animate-spin text-stone-400 mx-auto mb-4" />
                        <p className="text-sm text-stone-400">
                          {/* The run kept going when the connection dropped, so
                              say what is actually happening rather than
                              re-describing the work. */}
                          {isReconnecting
                            ? "Reconnecting — your plan is still being built..."
                            : "Analyzing your documents and creating a plan..."}
                        </p>
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-8 w-8 text-stone-300 mx-auto mb-4" />
                        <p className="text-sm text-stone-400">
                          The draft plan will appear here once ready.
                        </p>
                      </>
                    )}
                  </div>
                </Card>
              )}

              {/* Generate Button for smaller screens */}
              {canGenerate && (
                <div className="mt-4 lg:hidden">
                  <Button
                    onClick={handleGenerateWorkStep}
                    className="w-full gap-2"
                    size="lg"
                  >
                    <Sparkles className="h-4 w-4" />
                    {isRefineMode ? "Save Changes" : "Generate Work Step"}
                  </Button>
                </div>
              )}
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {/* Generating Overlay */}
      {isGenerating && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="max-w-sm">
            <CardContent className="pt-6">
              <div className="text-center">
                <Loader2 className="h-10 w-10 animate-spin text-stone-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">
                  Generating Work Step
                </h3>
                <p className="text-sm text-stone-500">
                  Creating your work step based on the approved plan...
                </p>
                {streamingContent && (
                  <p className="text-xs text-stone-400 mt-4 max-h-20 overflow-y-auto">
                    {streamingContent}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      <RefineOverwriteConfirmDialog
        conflict={overwriteConflict}
        isFinalizing={isGenerating}
        onConfirm={() => finalizeRefine(true)}
        onCancel={() => setOverwriteConflict(null)}
      />
    </div>
  );
};

export default CollabWorkStepPage;
